import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Scalar helpers
 */
const UUIDSchema = z.uuid();

/**
 * Payment method enum - mirrors PaymentMethodType from backend model
 */
export const SupplierPaymentMethodSchema = z.enum([
  "ach",
  "wire",
  "check",
  "card",
  "fednow",
  "credit_card",
  "other",
]);

/**
 * Supplier Payment Details response schema.
 * Bank account/routing numbers are returned masked (last 4 digits only).
 */
export const SupplierPaymentDetailsSchema = z.object({
  id: UUIDSchema,
  supplierId: UUIDSchema,
  method: SupplierPaymentMethodSchema,
  isDefault: z.boolean(),
  label: z.string().nullable(),
  bankName: z.string().nullable(),
  /** Last 4 digits only, e.g. "****1234" */
  accountNumberMasked: z.string().nullable(),
  /** Last 4 digits only, e.g. "****5678" */
  routingNumberMasked: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const ListSupplierPaymentDetailsInputSchema = z.object({
  supplierId: UUIDSchema,
});

export const ListSupplierPaymentDetailsResponseSchema = z.object({
  paymentDetails: z.array(SupplierPaymentDetailsSchema),
});

// ---------------------------------------------------------------------------
// Get
// ---------------------------------------------------------------------------

export const GetSupplierPaymentDetailsInputSchema = z.object({
  id: UUIDSchema,
});

export const GetSupplierPaymentDetailsResponseSchema =
  SupplierPaymentDetailsSchema;

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export const CreateSupplierPaymentDetailsInputSchema = z.object({
  supplierId: UUIDSchema,
  method: SupplierPaymentMethodSchema,
  isDefault: z.boolean().optional(),
  label: z.string().optional(),
  bankName: z.string().optional(),
  /** Plaintext account number - will be encrypted server-side */
  accountNumber: z
    .string()
    .regex(/^\d+$/, "Account number must contain only digits")
    .optional(),
  /** Plaintext routing number - will be encrypted server-side */
  routingNumber: z
    .string()
    .regex(/^\d{9}$/, "Routing number must be exactly 9 digits")
    .optional(),
});

export const CreateSupplierPaymentDetailsResponseSchema =
  SupplierPaymentDetailsSchema;

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

// NOTE: account/routing numbers are intentionally NOT editable. Bank numbers
// are immutable once created — to change them, delete this record and create a
// new one. This prevents silently redirecting funds on an existing record.
export const UpdateSupplierPaymentDetailsInputSchema = z.object({
  id: UUIDSchema,
  method: SupplierPaymentMethodSchema.optional(),
  isDefault: z.boolean().optional(),
  label: z.string().nullable().optional(),
  bankName: z.string().nullable().optional(),
});

export const UpdateSupplierPaymentDetailsResponseSchema =
  SupplierPaymentDetailsSchema;

// ---------------------------------------------------------------------------
// Delete (soft delete)
// ---------------------------------------------------------------------------

export const DeleteSupplierPaymentDetailsInputSchema = z.object({
  id: UUIDSchema,
});

export const DeleteSupplierPaymentDetailsResponseSchema = z.object({
  success: z.boolean(),
});

// ---------------------------------------------------------------------------
// Contract router
// ---------------------------------------------------------------------------

export const supplierPaymentDetailsContracts = {
  list: oc
    .input(ListSupplierPaymentDetailsInputSchema)
    .output(ListSupplierPaymentDetailsResponseSchema)
    .route({
      method: "GET",
      summary: "List supplier bank details",
      description:
        "Lists a supplier's payment details (method, label, bank, default flag) with account and routing numbers masked to the last four digits.",
    }),

  get: oc
    .input(GetSupplierPaymentDetailsInputSchema)
    .output(GetSupplierPaymentDetailsResponseSchema)
    .route({
      method: "GET",
      summary: "Get supplier bank details",
      description:
        "Returns one supplier payment-details record with method, bank name, default flag, and masked account and routing numbers.",
    }),

  create: oc
    .input(CreateSupplierPaymentDetailsInputSchema)
    .output(CreateSupplierPaymentDetailsResponseSchema)
    .route({
      method: "POST",
      summary: "Add supplier bank details",
      description:
        "Adds a payment method for a supplier, encrypting account and routing numbers and registering the bank account with the payment processor. Can become the supplier default.",
    }),

  update: oc
    .input(UpdateSupplierPaymentDetailsInputSchema)
    .output(UpdateSupplierPaymentDetailsResponseSchema)
    .route({
      method: "POST",
      summary: "Update supplier bank details",
      description:
        "Edits a supplier payment record's method, label, bank name, or default flag. Account and routing numbers cannot change; delete and re-add instead.",
    }),

  delete: oc
    .input(DeleteSupplierPaymentDetailsInputSchema)
    .output(DeleteSupplierPaymentDetailsResponseSchema)
    .route({
      method: "POST",
      summary: "Delete supplier bank details",
      description:
        "Soft-deletes a supplier payment-details record. Blocked while an approved payment still depends on it.",
    }),
} as const;

// ---------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------

export type SupplierPaymentDetails = z.infer<
  typeof SupplierPaymentDetailsSchema
>;
export type ListSupplierPaymentDetailsInput = z.infer<
  typeof ListSupplierPaymentDetailsInputSchema
>;
export type ListSupplierPaymentDetailsResponse = z.infer<
  typeof ListSupplierPaymentDetailsResponseSchema
>;
export type GetSupplierPaymentDetailsInput = z.infer<
  typeof GetSupplierPaymentDetailsInputSchema
>;
export type CreateSupplierPaymentDetailsInput = z.infer<
  typeof CreateSupplierPaymentDetailsInputSchema
>;
export type UpdateSupplierPaymentDetailsInput = z.infer<
  typeof UpdateSupplierPaymentDetailsInputSchema
>;
export type DeleteSupplierPaymentDetailsInput = z.infer<
  typeof DeleteSupplierPaymentDetailsInputSchema
>;
